import React, { useState, useEffect, useMemo, useRef } from "react";
import { FaMinus, FaPlus, FaCheck, FaWhatsapp, FaEnvelope, FaExpand, FaLayerGroup, FaTint, FaTag, FaPalette, FaFileAlt, FaArrowLeft } from "react-icons/fa";
import { FaTruckFast, FaBox } from "react-icons/fa6";
import { LuChevronDown } from "react-icons/lu";
import { PriceWithUnit } from "./PriceWithUnit";
import axios from "../../lib/axios";

const formatRupees = (value) => Number(value || 0).toLocaleString();

/* ── Editable Quantity Stepper ── */
const QuantityStepper = ({ value, min, max, step, onChange }) => {
  const canDecrease = value > min;
  const canIncrease = value < max;

  const handleInputChange = (e) => {
    const raw = e.target.value.replace(/\D/g, "");
    if (raw === "") return;
    const num = parseInt(raw, 10);
    if (!isNaN(num)) {
      const clamped = Math.max(min, Math.min(max, num));
      const stepped = Math.round((clamped - min) / step) * step + min;
      onChange(Math.max(min, Math.min(max, stepped)));
    }
  };

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => canDecrease && onChange(value - step)}
        disabled={!canDecrease}
        className="flex size-4 shrink-0 items-center justify-center rounded-full bg-brand-black text-white transition-colors hover:bg-gray-800 disabled:opacity-50 sm:size-7"
      >
        <FaMinus className="text-xs" />
      </button>
      <input
        type="text"
        inputMode="numeric"
        value={value}
        onChange={handleInputChange}
        className="h-8 w-12 rounded-lg border border-gray-200 bg-white text-center text-sm font-semibold tabular-nums text-gray-900 outline-none transition focus:border-[#e53935]"
      />
      <button
        type="button"
        onClick={() => canIncrease && onChange(value + step)}
        disabled={!canIncrease}
        className="flex size-4 shrink-0 items-center justify-center rounded-full bg-brand-black text-white transition-colors hover:bg-gray-800 disabled:opacity-50 sm:size-7"
      >
        <FaPlus className="text-xs" />
      </button>
    </div>
  );
};

/* ── Radio Group (pill style) ── */
// const RadioGroup = ({ options, value, onChange, name }) => {
// if (!Array.isArray(options) || options.length === 0) return null;
//
// return (
//   <div className="flex flex-wrap gap-2">
//     {options.map((option) => {
//       const optionValue = option?.value || option;
//       const optionLabel = option?.label || option;
//       const isSelected = value === optionValue;
//
//       return (
//         <label
//           key={optionValue}
//           className={`flex cursor-pointer items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-all ${isSelected
//             ? "border-neutral-900 bg-neutral-900 text-white"
//             : "border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300"
//             }`}
//         >
//           <input
//             type="radio"
//             name={name}
//             value={optionValue}
//             checked={isSelected}
//             onChange={() => onChange(optionValue)}
//             className="hidden"
//           />
//           <span
//             className={`flex h-2.5 w-2.5 items-center justify-center rounded-full border ${isSelected ? "border-white/60" : "border-neutral-400"
//               }`}
//           >
//             {isSelected && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
//           </span>
//           <span className="whitespace-nowrap">{optionLabel}</span>
//         </label>
//       );
//     })}
//   </div>
// );
// };

/* ── Reusable Dropdown (matches OptionRow style) ── */
const CustomDropdown = ({ label, options, value, onChange, renderTrigger, renderOption }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  if (!Array.isArray(options) || options.length === 0) return null;

  const selected = options.find((o, i) => i === value || (o?.value || o) === value);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-2.5 py-2 text-left transition-all outline-none hover:border-gray-300"
      >
        {renderTrigger ? renderTrigger(selected, isOpen) : (
          <>
            <span className="text-xs font-bold text-gray-800">{label}</span>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-600">
                {selected?.label || selected?.name || selected || value}
              </span>
              <span className={`flex size-5 shrink-0 items-center justify-center rounded-full bg-black text-white transition-transform ${isOpen ? "rotate-180" : ""}`}>
                <LuChevronDown className="text-xs" />
              </span>
            </div>
          </>
        )}
      </button>

      {isOpen && (
        <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-20 overflow-hidden rounded-lg border border-gray-100 bg-white shadow-lg">
          {options.map((opt, index) => {
            const optValue = opt?.value || opt;
            const isSelected = (typeof value === 'number' ? index === value : (opt?.value || opt) === value);

            return (
              <button
                key={optValue || index}
                type="button"
                onClick={() => {
                  onChange(typeof value === 'number' ? index : optValue);
                  setIsOpen(false);
                }}
                className={`flex w-full items-center justify-between px-3 py-2 text-left text-xs transition-colors ${isSelected
                  ? "bg-neutral-900 text-white"
                  : "text-gray-700 hover:bg-gray-50"
                  }`}
              >
                {renderOption ? renderOption(opt, isSelected) : (
                  <span className="flex items-center gap-2">
                    <span className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border text-[8px] ${isSelected ? "border-white/30 bg-white text-neutral-900" : "border-gray-200 bg-gray-50 text-transparent"}`}>
                      <FaCheck />
                    </span>
                    <span className="font-medium">{opt?.label || opt?.name || opt}</span>
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ── Product Option Row (icon + card style) ── */
const OptionRow = ({ label, options, value, onChange, removePrice = false, hideTriggerPrice = false, icon: Icon, defaultUnit = "piece", productData }) => {
  if (!Array.isArray(options) || options.length === 0) return null;
  return (
    <CustomDropdown
      label={label}
      options={options}
      value={value}
      onChange={onChange}
      renderTrigger={(selected, isOpen) => {
        const optionLabel = selected?.label || selected?.name || selected || "Select";
        const rawPrice = selected?.price_modifier ?? selected?.price;
        const showPriceFlag = Boolean(
          selected?.show_price ||
          selected?.show_price_in_options ||
          selected?.show_pricing ||
          (label === "Size" && productData?.show_size_prices) ||
          (label === "Material" && productData?.show_material_prices) ||
          (label === "Printing Side" && productData?.show_printing_side_prices) ||
          (label === "Colour" && productData?.show_colour_prices) ||
          (label === "Finish" && productData?.show_finishing_prices)
        );
        const hasPrice = !removePrice && !hideTriggerPrice && showPriceFlag && rawPrice != null && Number(rawPrice) !== 0;
        const unitStr = selected?.unit || defaultUnit;
        const formattedPrice = hasPrice ? `₹${formatRupees(rawPrice)}${unitStr ? `/${unitStr}` : ""}` : "";
        const isRecommended = Boolean(selected?.is_recommended || selected?.recommended);

        return (
          <div className="flex w-full items-center gap-3">
            {Icon && (
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#fce8e6] text-[#e53935]">
                <Icon className="text-xs" />
              </div>
            )}
            <div className="flex min-w-0 flex-1 flex-col gap-0">
              <span className="text-[11px] font-medium text-gray-500 leading-tight">{label}</span>
              <div className="flex items-center gap-2 truncate">
                <span className="truncate text-sm font-medium text-gray-900 leading-tight">
                  {optionLabel}
                </span>
                {isRecommended && (
                  <span className="inline-flex items-center rounded-full bg-[#ffe7ea] px-2 py-0.5 text-[9px] font-semibold tracking-[0.08em] text-[#bc1535]">
                    Recommended
                  </span>
                )}
                {hasPrice && (
                  <span className="text-xs font-bold text-[#d32f2f] whitespace-nowrap">
                    {formattedPrice}
                  </span>
                )}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1 self-center">
              <span className={`flex items-center justify-center text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}>
                <LuChevronDown className="text-xs" />
              </span>
            </div>
          </div>
        );
      }}
      renderOption={(opt, isSelected) => {
        const optionLabel = opt?.label || opt?.name || opt;
        const rawPrice = opt?.price_modifier ?? opt?.price;
        const showPriceFlag = Boolean(
          opt?.show_price ||
          opt?.show_price_in_options ||
          opt?.show_pricing ||
          (label === "Size" && productData?.show_size_prices) ||
          (label === "Material" && productData?.show_material_prices) ||
          (label === "Printing Side" && productData?.show_printing_side_prices) ||
          (label === "Colour" && productData?.show_colour_prices) ||
          (label === "Finish" && productData?.show_finishing_prices)
        );
        const hasPrice = !removePrice && showPriceFlag && rawPrice != null && Number(rawPrice) !== 0;
        const unitStr = opt?.unit || defaultUnit;
        const formattedPrice = hasPrice ? `₹${formatRupees(rawPrice)}${unitStr ? `/${unitStr}` : ""}` : "";
        const isRecommended = Boolean(opt?.is_recommended || opt?.recommended);

        return (
          <div className="flex w-full items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              <span className={`flex size-3.5 shrink-0 items-center justify-center rounded-full border text-[8px] ${isSelected ? "border-white/30 bg-white text-neutral-900" : "border-gray-200 bg-gray-50 text-transparent"}`}>
                <FaCheck />
              </span>
              <span className="font-medium">{optionLabel}</span>
              {isRecommended && (
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-semibold tracking-[0.08em] ${isSelected ? "bg-white/15 text-white" : "bg-[#ffe7ea] text-[#bc1535]"}`}>
                  Recommended
                </span>
              )}
            </span>
            {hasPrice && (
              <span className={`text-[10px] font-bold ${isSelected ? "text-white" : "text-[#d32f2f]"}`}>
                {formattedPrice}
              </span>
            )}
          </div>
        );
      }}
    />
  );
};

/* ── Helper: Resolve custom size rate per unit area ── */
const getCustomUnitRate = (unit, customSizeConfig = {}, productData = {}, deliveryOpt = null) => {
  const u = (unit || "inch").toLowerCase().trim();
  const config = customSizeConfig || {};

  // 1. Direct match in customSizeConfig
  const sqFtRate = Number(config.price_per_sq_ft ?? productData?.price_per_sq_ft);
  const sqInRate = Number(config.price_per_sq_inch ?? productData?.price_per_sq_inch);
  const sqCmRate = Number(config.price_per_sq_cm ?? productData?.price_per_sq_cm);
  const sqMmRate = Number(config.price_per_sq_mm ?? productData?.price_per_sq_mm);

  if ((u === "ft" || u === "feet") && sqFtRate > 0) return sqFtRate;
  if ((u === "inch" || u === "in") && sqInRate > 0) return sqInRate;
  if (u === "cm" && sqCmRate > 0) return sqCmRate;
  if (u === "mm" && sqMmRate > 0) return sqMmRate;

  // 2. Cross-conversion from any configured unit
  if (sqFtRate > 0) {
    if (u === "inch" || u === "in") return sqFtRate / 144;
    if (u === "cm") return sqFtRate / 929.0304;
    if (u === "mm") return sqFtRate / 92903.04;
    return sqFtRate;
  }
  if (sqInRate > 0) {
    if (u === "ft" || u === "feet") return sqInRate * 144;
    if (u === "cm") return sqInRate / 6.4516;
    if (u === "mm") return sqInRate / 645.16;
    return sqInRate;
  }
  if (sqCmRate > 0) {
    if (u === "ft" || u === "feet") return sqCmRate * 929.0304;
    if (u === "inch" || u === "in") return sqCmRate * 6.4516;
    if (u === "mm") return sqCmRate / 100;
    return sqCmRate;
  }
  if (sqMmRate > 0) {
    if (u === "ft" || u === "feet") return sqMmRate * 92903.04;
    if (u === "inch" || u === "in") return sqMmRate * 645.16;
    if (u === "cm") return sqMmRate * 100;
    return sqMmRate;
  }

  // 3. Fallback to delivery option price or product base price
  const rawBase = Number(deliveryOpt?.price) > 0
    ? Number(deliveryOpt.price)
    : (Number(productData?.price) > 0 ? Number(productData.price) : 0);

  const productUnit = (deliveryOpt?.unit || productData?.unit || "").toLowerCase().trim();

  if (rawBase > 0) {
    if (productUnit.includes("sq ft") || productUnit.includes("sqft") || productUnit.includes("feet") || productUnit === "ft") {
      if (u === "ft" || u === "feet") return rawBase;
      if (u === "inch" || u === "in") return rawBase / 144;
      if (u === "cm") return rawBase / 929.0304;
      if (u === "mm") return rawBase / 92903.04;
    } else if (productUnit.includes("sq in") || productUnit.includes("sqin") || productUnit.includes("inch") || productUnit === "in") {
      if (u === "inch" || u === "in") return rawBase;
      if (u === "ft" || u === "feet") return rawBase * 144;
      if (u === "cm") return rawBase / 6.4516;
      if (u === "mm") return rawBase / 645.16;
    } else {
      // Default: base price applies to active unit directly
      return rawBase;
    }
  }

  return 0;
};

/* ── Delivery Option Dropdown (icon + card style) ── */
const DeliveryDropdown = ({
  options,
  value,
  onChange,
  priceModifiers = 0,
  label = "Delivery Option",
  isBulk = false,
  defaultUnit = "piece",
  isCustomSize = false,
  customUnitRate = 0,
  unitAreaLabel = "sq ft",
}) => {
  if (!Array.isArray(options) || options.length === 0) return null;

  return (
    <CustomDropdown
      options={options}
      value={value}
      onChange={onChange}
      renderTrigger={(opt, isOpen) => {
        const optionPrice = isCustomSize
          ? customUnitRate
          : (Number(opt?.price) || 0) + priceModifiers;
        const origPrice = Number(opt?.original_price) || 0;
        const hasSavings = isBulk && origPrice > Number(opt?.price || 0);
        const savings = hasSavings ? origPrice - Number(opt?.price || 0) : 0;
        const isRecommended = Boolean(opt?.is_recommended || opt?.recommended);

        return (
          <div className="flex w-full items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#fce8e6] text-[#e53935]">
              {isBulk ? <FaBox className="text-xs" /> : <FaTruckFast className="text-xs" />}
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-0">
              <span className="text-[11px] font-medium text-gray-500 leading-tight">{label}</span>
              <div className="flex items-center gap-2 truncate">
                <span className="truncate text-sm font-medium text-gray-900 leading-tight">
                  {opt?.name || "Select option"}
                </span>
                {isRecommended && (
                  <span className="inline-flex items-center rounded-full bg-[#ffe7ea] px-2 py-0.5 text-[9px] font-semibold tracking-[0.08em] text-[#bc1535]">
                    Recommended
                  </span>
                )}
                {hasSavings && (
                  <span className="rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-bold text-green-700">
                    Save ₹{formatRupees(savings)}
                  </span>
                )}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1.5 self-center">
              {hasSavings && (
                <span className="text-xs text-gray-400 line-through">
                  ₹{formatRupees(origPrice + priceModifiers)}
                </span>
              )}
              <span className="text-xs font-bold text-[#d32f2f]">
                ₹{formatRupees(optionPrice)}
                {isCustomSize ? `/${unitAreaLabel}` : ""}
              </span>
              <span className={`flex items-center justify-center text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}>
                <LuChevronDown className="text-xs" />
              </span>
            </div>
          </div>
        );
      }}
      renderOption={(opt, isSelected) => {
        const min = opt?.min_quantity || 1;
        const max = opt?.max_quantity;
        const range = max ? `Qty ${min}-${max}` : `Qty ${min}+`;
        const showRange = !isBulk && !isCustomSize && (opt?.min_quantity != null || opt?.max_quantity != null);
        const optionPrice = isCustomSize
          ? customUnitRate
          : (Number(opt?.price) || 0) + priceModifiers;
        const origPrice = Number(opt?.original_price) || 0;
        const hasSavings = isBulk && origPrice > Number(opt?.price || 0);
        const savings = hasSavings ? origPrice - Number(opt?.price || 0) : 0;
        const savingsPercent = hasSavings ? Math.round((savings / origPrice) * 100) : 0;
        const displayUnit = isCustomSize ? unitAreaLabel : (opt?.unit || defaultUnit);
        const isRecommended = Boolean(opt?.is_recommended || opt?.recommended);

        return (
          <span className="flex w-full items-center justify-between gap-2">
            <span className="flex items-center gap-2">
              <span className={`flex size-3.5 shrink-0 items-center justify-center rounded-full border text-[8px] ${isSelected ? "border-white/30 bg-white text-neutral-900" : "border-gray-200 bg-gray-50 text-transparent"}`}>
                <FaCheck />
              </span>
              <span className="font-medium">{opt?.name || "Option"}</span>
              {isRecommended && (
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-semibold tracking-[0.08em] ${isSelected ? "bg-white/15 text-white" : "bg-[#ffe7ea] text-[#bc1535]"}`}>
                  Recommended
                </span>
              )}
            </span>
            <div className="flex items-center gap-2">
              {hasSavings && (
                <span className={`text-[10px] ${isSelected ? "text-green-300" : "text-green-600 font-semibold"}`}>
                  ({savingsPercent}% OFF • Save ₹{formatRupees(savings)})
                </span>
              )}
              {hasSavings && (
                <span className={`text-[10px] line-through ${isSelected ? "text-white/60" : "text-gray-400"}`}>
                  ₹{formatRupees(origPrice + priceModifiers)}
                </span>
              )}
              <span className={`text-[10px] font-bold ${isSelected ? "text-white" : "text-gray-700"}`}>
                ₹{formatRupees(optionPrice)}
                {!isBulk ? ` / ${displayUnit}` : ""}
                {showRange ? ` • ${range}` : ""}
              </span>
            </div>
          </span>
        );
      }}
    />
  );
};

/* ──────────────────────────────────────────────────────────────
   MAIN COMPONENT
   ────────────────────────────────────────────────────────────── */

export const PrintingCart = ({
  data,
  onAddToCart,
  isLoggedIn,
  onLoginRequired,
  externalDeliveryMode,
  onDeliveryModeChange,
}) => {
  // ─── Custom Size Config ───
  const customSizeConfig = data?.custom_size_config || {};
  const allowCustomSize = Boolean(customSizeConfig?.allow_custom_size);
  const supportedUnits = customSizeConfig?.supported_units || ["inch"];

  // ─── Raw option arrays ───
  const sizeOptions = data?.size_options ? [...data.size_options] : [];
  const showPresetSizes = data?.show_size_options !== false && sizeOptions.length > 0;

  const hasBothSizeModes = showPresetSizes && allowCustomSize;
  const showOnlyCustomSize = !showPresetSizes && allowCustomSize;
  const showOnlyPresetSizes = showPresetSizes && !allowCustomSize;
  const hasAnySizeOption = showPresetSizes || allowCustomSize;

  const materialOptions = data?.material_options || [];
  const printingSideOptions = data?.printing_side_options || [];
  const colourOptions = data?.colour_options || [];
  const finishingOptions = data?.finishing_options || [];

  // ─── Delivery config ───
  const weekdayDelivery = data?.weekday_delivery || {};
  const expressDelivery = data?.express_delivery || {};
  const weekdayOptions = weekdayDelivery.options || [];
  const expressOptions = expressDelivery.options || [];
  const deliveryCharge = Number(data?.delivery_charge ?? 120);
  const deliveryPricingTiers = useMemo(
    () =>
      (Array.isArray(data?.delivery_pricing_tiers)
        ? data.delivery_pricing_tiers
        : []
      )
        .map((tier) => ({
          label: tier?.label,
          limit: Number(tier?.up_to_sqft),
          price: Number(tier?.price),
        }))
        .filter((tier) => Number.isFinite(tier.limit) && Number.isFinite(tier.price))
        .sort((a, b) => a.limit - b.limit),
    [data?.delivery_pricing_tiers]
  );

  const defaultWeekdayIndex = Math.max(0, weekdayOptions.findIndex((o) => o.is_default));
  const defaultExpressIndex = Math.max(0, expressOptions.findIndex((o) => o.is_default));

  // ─── State ───
  const [deliveryMode, setDeliveryMode] = useState(
    externalDeliveryMode || (expressDelivery.available ? "express" : "weekday")
  );

  useEffect(() => {
    if (externalDeliveryMode) {
      setDeliveryMode(externalDeliveryMode);
    }
  }, [externalDeliveryMode]);

  const handleDeliveryModeChange = (mode) => {
    setDeliveryMode(mode);
    onDeliveryModeChange?.(mode);
  };

  const defaultUnit = data?.unit?.trim() || "piece";

  const [sizeMode, setSizeMode] = useState(
    showOnlyCustomSize ? "custom" : "presets"
  );
  const [selectedSize, setSelectedSize] = useState(sizeOptions[0]?.value || "");

  const sizeDropdownOptions = useMemo(() => {
    if (!hasBothSizeModes) return sizeOptions;
    return [
      ...sizeOptions,
      { label: "✏️ Custom Dimensions...", value: "__custom__" }
    ];
  }, [sizeOptions, hasBothSizeModes]);

  const [selectedMaterial, setSelectedMaterial] = useState(materialOptions[0]?.value || "");
  const [selectedPrintingSide, setSelectedPrintingSide] = useState(
    printingSideOptions.find((o) => o.is_default)?.value || printingSideOptions[0]?.value || ""
  );
  const [selectedColour, setSelectedColour] = useState(colourOptions[0]?.value || "");
  const [selectedFinishing, setSelectedFinishing] = useState(finishingOptions[0]?.value || "");
  const [fileOption, setFileOption] = useState("email");

  // ─── Dynamic Finishing options filtering by Selected Size ───
  const filteredFinishingOptions = useMemo(() => {
    if (!Array.isArray(finishingOptions) || finishingOptions.length === 0) return [];
    if (!showPresetSizes || sizeMode !== "presets" || !selectedSize) return finishingOptions;

    const currentSizeObj = sizeOptions.find((o) => (o?.value || o?.label || o) === selectedSize);
    const sizeValue = (currentSizeObj?.value || typeof selectedSize === "string" ? selectedSize : "").toString().trim().toLowerCase();
    const sizeLabel = (currentSizeObj?.label || "").toString().trim().toLowerCase();

    const sizeAllowedFinishingRaw = currentSizeObj?.allowed_finishing_options || currentSizeObj?.allowedFinishingOptions || "";
    const sizeAllowedFinishingList = typeof sizeAllowedFinishingRaw === "string" && sizeAllowedFinishingRaw.trim()
      ? sizeAllowedFinishingRaw.split(",").map(s => s.trim().toLowerCase()).filter(Boolean)
      : (Array.isArray(sizeAllowedFinishingRaw) ? sizeAllowedFinishingRaw.map(s => (s?.value || s?.label || s || "").toString().trim().toLowerCase()).filter(Boolean) : null);

    return finishingOptions.filter((finOpt) => {
      const finValue = (finOpt?.value || typeof finOpt === "string" ? finOpt : "").toString().trim().toLowerCase();
      const finLabel = (finOpt?.label || "").toString().trim().toLowerCase();

      // 1. If size restricts allowed finishing options
      if (sizeAllowedFinishingList && sizeAllowedFinishingList.length > 0) {
        const matchesSizeRule = sizeAllowedFinishingList.includes(finValue) || sizeAllowedFinishingList.includes(finLabel);
        if (!matchesSizeRule) return false;
      }

      // 2. If finishing option restricts allowed sizes
      const finAllowedSizesRaw = finOpt?.allowed_sizes || finOpt?.allowedSizes || "";
      if (finAllowedSizesRaw) {
        const finAllowedSizesList = typeof finAllowedSizesRaw === "string" && finAllowedSizesRaw.trim()
          ? finAllowedSizesRaw.split(",").map(s => s.trim().toLowerCase()).filter(Boolean)
          : (Array.isArray(finAllowedSizesRaw) ? finAllowedSizesRaw.map(s => (s?.value || s?.label || s || "").toString().trim().toLowerCase()).filter(Boolean) : []);
        if (finAllowedSizesList.length > 0) {
          const matchesFinRule = finAllowedSizesList.includes(sizeValue) || finAllowedSizesList.includes(sizeLabel);
          if (!matchesFinRule) return false;
        }
      }

      return true;
    });
  }, [finishingOptions, sizeOptions, selectedSize, sizeMode]);

  // Ensure selectedFinishing is always valid when filtered options change
  useEffect(() => {
    if (filteredFinishingOptions.length > 0) {
      const isCurrentValid = filteredFinishingOptions.some(
        (o) => (o?.value || o?.label || o) === selectedFinishing
      );
      if (!isCurrentValid) {
        setSelectedFinishing(filteredFinishingOptions[0]?.value || filteredFinishingOptions[0]?.label || "");
      }
    } else if (selectedFinishing !== "") {
      setSelectedFinishing("");
    }
  }, [filteredFinishingOptions, selectedFinishing]);

  // ─── Custom size state ───
  const [customWidth, setCustomWidth] = useState(
    customSizeConfig?.min_width || (supportedUnits[0] === "ft" ? 2 : 12)
  );
  const [customHeight, setCustomHeight] = useState(
    customSizeConfig?.min_height || (supportedUnits[0] === "ft" ? 2 : 12)
  );
  const [customUnit, setCustomUnit] = useState(supportedUnits[0] || "inch");

  const [selectedWeekdayOptionIndex, setSelectedWeekdayOptionIndex] = useState(defaultWeekdayIndex);
  const [selectedExpressOptionIndex, setSelectedExpressOptionIndex] = useState(defaultExpressIndex);
  const [quantity, setQuantity] = useState(1);

  // ─── Special Requirement state ───
  const [specialRequirementText, setSpecialRequirementText] = useState("");

  // ─── Coupon state ───
  const [couponCode, setCouponCode] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponError, setCouponError] = useState("");
  const [appliedDiscountPercentage, setAppliedDiscountPercentage] = useState(0);

  // ─── Reset when data changes ───
  useEffect(() => {
    setDeliveryMode(expressDelivery.available ? "express" : "weekday");
    setSizeMode(showOnlyCustomSize ? "custom" : "presets");
    setSelectedSize(sizeOptions[0]?.value || "");
    setCustomWidth(customSizeConfig?.min_width || (supportedUnits[0] === "ft" ? 2 : 12));
    setCustomHeight(customSizeConfig?.min_height || (supportedUnits[0] === "ft" ? 2 : 12));
    setCustomUnit(supportedUnits[0] || "inch");
    setSelectedMaterial(materialOptions[0]?.value || "");
    setSelectedPrintingSide(
      printingSideOptions.find((o) => o.is_default)?.value || printingSideOptions[0]?.value || ""
    );
    setSelectedColour(colourOptions[0]?.value || "");
    setSelectedFinishing(finishingOptions[0]?.value || "");
    setFileOption("email");
    setSelectedWeekdayOptionIndex(defaultWeekdayIndex);
    setSelectedExpressOptionIndex(defaultExpressIndex);
    setQuantity(1);
    setSpecialRequirementText("");
    setCouponCode("");
    setCouponApplied(false);
    setCouponError("");
    setAppliedDiscountPercentage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id, data?.slug]);

  // ─── Clamp quantity when option or delivery mode changes ───
  useEffect(() => {
    if (deliveryMode !== "express" && deliveryMode !== "weekday") return;
    const opt = deliveryMode === "express" ? expressOptions[selectedExpressOptionIndex] : weekdayOptions[selectedWeekdayOptionIndex];
    if (!opt) return;
    const min = opt.min_quantity != null && !isNaN(opt.min_quantity) && Number(opt.min_quantity) > 0 ? Number(opt.min_quantity) : 1;
    const max = opt.max_quantity != null && !isNaN(opt.max_quantity) && Number(opt.max_quantity) > 0 ? Number(opt.max_quantity) : 10000;
    const step = opt.quantity_step != null && !isNaN(opt.quantity_step) && Number(opt.quantity_step) > 0 ? Number(opt.quantity_step) : 1;
    setQuantity((prev) => {
      let next = Math.max(min, Math.min(max, prev));
      next = Math.round((next - min) / step) * step + min;
      return Math.max(min, Math.min(max, next));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExpressOptionIndex, selectedWeekdayOptionIndex, deliveryMode]);

  // ─── Active options ───
  const selectedWeekdayOption = weekdayOptions[selectedWeekdayOptionIndex] || null;
  const selectedExpressOption = expressOptions[selectedExpressOptionIndex] || null;

  const isCustomSizeActive = (sizeMode === "custom" || showOnlyCustomSize) && allowCustomSize;

  const unitAreaLabel = useMemo(() => {
    const u = (customUnit || "inch").toLowerCase().trim();
    if (u === "ft" || u === "feet") return "sq ft";
    if (u === "inch" || u === "in") return "sq in";
    if (u === "cm") return "sq cm";
    if (u === "mm") return "sq mm";
    return `sq ${u}`;
  }, [customUnit]);

  const numWidth = Math.max(0, parseFloat(customWidth) || 0);
  const numHeight = Math.max(0, parseFloat(customHeight) || 0);

  const customArea = useMemo(() => {
    const rawArea = numWidth * numHeight;
    return Number(rawArea.toFixed(4));
  }, [numWidth, numHeight]);

  // ─── Size-based delivery pricing tiers ───
  const customAreaSqFt = useMemo(() => {
    const u = (customUnit || "inch").toLowerCase().trim();
    const sqFtFactor =
      u === "ft" || u === "feet"
        ? 1
        : u === "inch" || u === "in"
        ? 1 / 144
        : u === "cm"
        ? 1 / 929.0304
        : u === "mm"
        ? 1 / 92903.04
        : 1;
    return customArea * sqFtFactor;
  }, [customArea, customUnit]);

  const totalAreaSqFt = isCustomSizeActive
    ? Number((customAreaSqFt * quantity).toFixed(2))
    : null;

  const { resolvedDeliveryCharge, activeDeliveryTier } = useMemo(() => {
    if (!deliveryPricingTiers.length || !isCustomSizeActive || totalAreaSqFt == null) {
      return { resolvedDeliveryCharge: deliveryCharge, activeDeliveryTier: null };
    }
    const matchedTier =
      deliveryPricingTiers.find((tier) => totalAreaSqFt <= tier.limit) ||
      deliveryPricingTiers[deliveryPricingTiers.length - 1];
    return {
      resolvedDeliveryCharge: matchedTier.price > 0 ? matchedTier.price : deliveryCharge,
      activeDeliveryTier: matchedTier,
    };
  }, [deliveryPricingTiers, isCustomSizeActive, totalAreaSqFt, deliveryCharge]);

  const customUnitRate = useMemo(() => {
    const activeDeliveryOpt = deliveryMode === "weekday" ? selectedWeekdayOption : selectedExpressOption;
    return getCustomUnitRate(customUnit, customSizeConfig, data, activeDeliveryOpt);
  }, [customUnit, customSizeConfig, data, deliveryMode, selectedWeekdayOption, selectedExpressOption]);

  const customSizePrice = useMemo(() => {
    if (!isCustomSizeActive) return 0;
    return Math.round(customArea * customUnitRate);
  }, [isCustomSizeActive, customArea, customUnitRate]);

  const currentUnit = useMemo(() => {
    if (isCustomSizeActive) {
      return unitAreaLabel;
    }
    return deliveryMode === "weekday"
      ? selectedWeekdayOption?.unit || defaultUnit
      : selectedExpressOption?.unit || defaultUnit;
  }, [isCustomSizeActive, unitAreaLabel, deliveryMode, selectedWeekdayOption, selectedExpressOption, defaultUnit]);

  // ─── Options modifiers (Material, Side, Colour, Finish) ───
  const optionsModifierTotal = useMemo(() => {
    let total = 0;
    const matOpt = materialOptions.find((o) => o.value === selectedMaterial);
    if (matOpt?.price_modifier) total += Number(matOpt.price_modifier);
    const sideOpt = printingSideOptions.find((o) => o.value === selectedPrintingSide);
    if (sideOpt?.price_modifier) total += Number(sideOpt.price_modifier);
    const colOpt = colourOptions.find((o) => o.value === selectedColour);
    if (colOpt?.price_modifier) total += Number(colOpt.price_modifier);
    const finOpt = filteredFinishingOptions.find((o) => o.value === selectedFinishing) || finishingOptions.find((o) => o.value === selectedFinishing);
    if (finOpt?.price_modifier) total += Number(finOpt.price_modifier);
    return total;
  }, [selectedMaterial, selectedPrintingSide, selectedColour, selectedFinishing, materialOptions, printingSideOptions, colourOptions, filteredFinishingOptions, finishingOptions]);

  // ─── Price modifiers ───
  const priceModifiers = useMemo(() => {
    if (isCustomSizeActive) {
      return customSizePrice + optionsModifierTotal;
    }
    const sizeOpt = sizeOptions.find((o) => o.value === selectedSize);
    const sizeMod = sizeOpt?.price_modifier ? Number(sizeOpt.price_modifier) : 0;
    return sizeMod + optionsModifierTotal;
  }, [isCustomSizeActive, customSizePrice, optionsModifierTotal, sizeOptions, selectedSize]);

  // ─── Subtotal & Total ───
  const { subtotal, unitPrice, qty } = useMemo(() => {
    if (isCustomSizeActive) {
      // For custom sizing: Unit item price is (Custom Area * Rate per sq unit) + options modifiers
      const unit = customSizePrice + optionsModifierTotal;
      const total = unit * quantity;
      return { subtotal: total, unitPrice: unit, qty: quantity };
    }

    if (deliveryMode === "weekday" && selectedWeekdayOption) {
      const base = Number(selectedWeekdayOption.price) || 0;
      const unit = base + priceModifiers;
      const total = unit * quantity;
      return { subtotal: total, unitPrice: unit, qty: quantity };
    }
    if (deliveryMode === "express" && selectedExpressOption) {
      const base = Number(selectedExpressOption.price) || 0;
      const unit = base + priceModifiers;
      const total = unit * quantity;
      return { subtotal: total, unitPrice: unit, qty: quantity };
    }

    // If no delivery options are configured or selected, use the price modifiers as the unit price
    if (deliveryMode !== "special_requirement") {
      const unit = priceModifiers;
      const total = unit * quantity;
      return { subtotal: total, unitPrice: unit, qty: quantity };
    }

    return { subtotal: 0, unitPrice: 0, qty: 1 };
  }, [
    isCustomSizeActive,
    customSizePrice,
    optionsModifierTotal,
    deliveryMode,
    selectedWeekdayOption,
    selectedExpressOption,
    quantity,
    priceModifiers,
  ]);

  // ─── Coupon discount ───
  const discountAmount = couponApplied ? subtotal * (appliedDiscountPercentage / 100) : 0;
  const discountedSubtotal = subtotal - discountAmount;
  const gst = discountedSubtotal * 0.18;
  const total = discountedSubtotal + gst + resolvedDeliveryCharge;

  // ─── Quantity bounds ───
  const activeDeliveryOption =
    deliveryMode === "weekday"
      ? selectedWeekdayOption
      : deliveryMode === "express"
      ? selectedExpressOption
      : null;

  const qtyMin = activeDeliveryOption && activeDeliveryOption.min_quantity != null && !isNaN(activeDeliveryOption.min_quantity) && Number(activeDeliveryOption.min_quantity) > 0
    ? Number(activeDeliveryOption.min_quantity)
    : 1;
  const qtyMax = activeDeliveryOption && activeDeliveryOption.max_quantity != null && !isNaN(activeDeliveryOption.max_quantity) && Number(activeDeliveryOption.max_quantity) > 0
    ? Number(activeDeliveryOption.max_quantity)
    : 10000;
  const qtyStep = activeDeliveryOption && activeDeliveryOption.quantity_step != null && !isNaN(activeDeliveryOption.quantity_step) && Number(activeDeliveryOption.quantity_step) > 0
    ? Number(activeDeliveryOption.quantity_step)
    : 1;

  // ─── Coupon handlers ───
  const handleApplyCoupon = async () => {
    setCouponError("");
    if (!couponCode.trim()) {
      setCouponApplied(false);
      return;
    }
    try {
      const response = await axios.post("/api/validate-coupon", {
        code: couponCode.trim(),
        collection: "printing",
        productId: data?.id
      });
      if (response.data?.valid) {
        setCouponApplied(true);
        setAppliedDiscountPercentage(response.data.discountPercentage || 0);
      } else {
        setCouponError("Invalid coupon code");
        setCouponApplied(false);
      }
    } catch (error) {
      setCouponError(error.response?.data?.error || "Error validating coupon");
      setCouponApplied(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode("");
    setCouponApplied(false);
    setCouponError("");
    setAppliedDiscountPercentage(0);
  };

  // ─── Auto report cart data ───
  useEffect(() => {
    const sizeDescription = isCustomSizeActive
      ? `Custom (${customWidth}x${customHeight} ${customUnit} - ${customArea} ${unitAreaLabel})`
      : selectedSize;

    const cartData = {
      name: data.title,
      description: `${data.product_type || data.title} - ${sizeDescription} - ${selectedMaterial || 'Standard'}`,
      image_url: data?.image?.cloudinary?.secure_url || data?.image?.url || "",
      quantity: {
        delivery: deliveryMode,
        qty: quantity,
        ...(deliveryMode === "weekday" && selectedWeekdayOption
          ? { weekday_option: selectedWeekdayOption.name, qty: quantity }
          : {}),
        ...(deliveryMode === "express" && selectedExpressOption
          ? { express_option: selectedExpressOption.name, qty: quantity }
          : {}),
        ...(deliveryMode === "special_requirement"
          ? { special_requirement: specialRequirementText }
          : {}),
        ...(isCustomSizeActive
          ? {
              size: `Custom (${customWidth}x${customHeight} ${customUnit})`,
              custom_dimensions: {
                width: numWidth,
                height: numHeight,
                unit: customUnit,
                area: customArea,
                unit_area_label: unitAreaLabel,
                rate_per_unit_area: customUnitRate,
              },
              ...(activeDeliveryTier
                ? {
                    total_area_sqft: totalAreaSqFt,
                    delivery_tier: activeDeliveryTier.label || `${activeDeliveryTier.limit} sq ft`,
                  }
                : {}),
            }
          : selectedSize ? { size: selectedSize } : {}),
        ...(selectedMaterial ? { material: selectedMaterial } : {}),
        ...(selectedPrintingSide ? { printing_side: selectedPrintingSide } : {}),
        ...(selectedColour ? { colour: selectedColour } : {}),
        ...(selectedFinishing ? { finishing: selectedFinishing } : {}),
        ...(fileOption ? { file_option: fileOption } : {}),
        ...(couponApplied ? { coupon_code: couponCode.trim().toUpperCase() } : {}),
      },
      subtotal,
      discount_amount: discountAmount,
      discount_percentage: couponApplied ? appliedDiscountPercentage : 0,
      delivery_charge: resolvedDeliveryCharge,
      tax_amount: gst,
      total_amount: total,
    };
    onAddToCart(cartData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deliveryMode,
    selectedWeekdayOptionIndex,
    selectedExpressOptionIndex,
    quantity,
    specialRequirementText,
    sizeMode,
    isCustomSizeActive,
    selectedSize,
    customWidth,
    customHeight,
    customUnit,
    customArea,
    customUnitRate,
    selectedMaterial,
    selectedPrintingSide,
    selectedColour,
    selectedFinishing,
    fileOption,
    couponApplied,
    couponCode,
    subtotal,
    discountAmount,
    appliedDiscountPercentage,
    deliveryCharge,
    resolvedDeliveryCharge,
    activeDeliveryTier,
    totalAreaSqFt,
    gst,
    total,
  ]);

  // ─── Delivery toggle helpers ───
  const showWeekdayToggle = weekdayDelivery.available;
  const showExpressToggle = expressDelivery.available;
  const isSpecialRequirement = deliveryMode === "special_requirement";

  const hasOptions =
    sizeOptions.length > 0 ||
    materialOptions.length > 0 ||
    printingSideOptions.length > 0 ||
    colourOptions.length > 0 ||
    finishingOptions.length > 0;

  return (
    <div className="w-full space-y-4">
      {/* Live Printing Active Status Indicator - Only shown for live printing product */}
      {(Boolean(data?.livePrintingBooking) && (data?.slug === "live-printing-service" || data?.slug === "real-time-printing-service")) && (
        <div className="flex items-center justify-between rounded-xl border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-xs font-semibold text-emerald-800">
          <div className="flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500"></span>
            </span>
            <span>Live Printing Active</span>
          </div>
          <span className="text-[11px] font-medium text-emerald-700">
            Timings: {data?.livePrintingSettings?.workingHours?.start || "09:00 AM"} – {data?.livePrintingSettings?.workingHours?.end || "06:00 PM"}
          </span>
        </div>
      )}

      {/* Delivery Mode Toggle or Back Button when Custom Requirement is active */}
      {isSpecialRequirement ? (
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => handleDeliveryModeChange(showExpressToggle ? "express" : "weekday")}
            className="inline-flex items-center gap-2 rounded-xl bg-black px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-gray-800"
          >
            <FaArrowLeft className="text-xs" />
            <span>Back</span>
          </button>
          <h3 className="text-base font-bold text-gray-900 mb-0">Custom Requirement</h3>
        </div>
      ) : (
        <div className="flex gap-3">
          {showExpressToggle && (
            <button
              type="button"
              onClick={() => handleDeliveryModeChange("express")}
              className={`group relative flex-1 overflow-hidden rounded-xl px-4 py-3.5 text-center transition-all duration-200 shadow-sm ${deliveryMode === "express"
                ? "bg-[#e53935] text-white"
                : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                }`}
            >
              {deliveryMode === "express" && (
                <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-transparent" />
              )}
              <div className="relative z-10 flex items-center justify-center gap-2.5">
                <FaTruckFast className="text-base shrink-0" />
                <span className="text-sm font-semibold whitespace-nowrap">Regular Order</span>
              </div>
            </button>
          )}
          {showWeekdayToggle && (
            <button
              type="button"
              onClick={() => handleDeliveryModeChange("weekday")}
              className={`group relative flex-1 overflow-hidden rounded-xl px-4 py-3.5 text-center transition-all duration-200 shadow-sm ${deliveryMode === "weekday"
                ? "bg-neutral-900 text-white"
                : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                }`}
            >
              <div className="relative z-10 flex items-center justify-center gap-2.5">
                <FaBox className="text-base shrink-0" />
                <span className="text-sm font-semibold whitespace-nowrap">Bulk Order</span>
              </div>
            </button>
          )}
        </div>
      )}

      {/* Special Requirement Textarea */}
      {isSpecialRequirement && (
        <div className="space-y-2">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-500">
            Your Requirement
          </p>
          <textarea
            value={specialRequirementText}
            onChange={(e) => setSpecialRequirementText(e.target.value)}
            placeholder="Describe your custom requirement here (size, material, quantity, design preferences, etc.)"
            rows={4}
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-xs font-medium text-gray-800 outline-none transition focus:border-red-400 resize-none"
          />
          <p className="text-[11px] text-gray-400">
            Our team will review your requirement and contact you shortly.
          </p>
        </div>
      )}

      {/* Product Options — vertical rows */}
      {!isSpecialRequirement && hasOptions && (
        <div className="space-y-2">
          <div className="space-y-1.5">
            {/* Size Section */}
            {hasAnySizeOption && (
              <div className="space-y-2">
                {hasBothSizeModes && (
                  <div className="mb-1.5 mt-1 flex items-center justify-between gap-2">
                    <span className="text-sm font-bold text-gray-900">साइज़ (Size)</span>
                    <div className="inline-flex rounded-lg bg-gray-100 p-1 border border-gray-200/80">
                      <button
                        type="button"
                        onClick={() => setSizeMode("presets")}
                        className={`px-3 py-1 text-xs font-semibold rounded-md transition-all duration-200 ${
                          sizeMode === "presets"
                            ? "bg-neutral-900 text-white shadow-sm"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        Fixed Size
                      </button>
                      <button
                        type="button"
                        onClick={() => setSizeMode("custom")}
                        className={`px-3 py-1 text-xs font-semibold rounded-md transition-all duration-200 ${
                          sizeMode === "custom"
                            ? "bg-[#e53935] text-white shadow-sm"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        Custom Size
                      </button>
                    </div>
                  </div>
                )}

                {showPresetSizes && (sizeMode === "presets" || !hasBothSizeModes) && sizeOptions.length > 0 && (
                  <OptionRow
                    label="Size"
                    options={sizeDropdownOptions}
                    value={selectedSize}
                    onChange={(val) => {
                      if (val === "__custom__") {
                        setSizeMode("custom");
                      } else {
                        setSelectedSize(val);
                      }
                    }}
                    icon={FaExpand}
                    defaultUnit={defaultUnit}
                    productData={data}
                  />
                )}

                {allowCustomSize && (sizeMode === "custom" || showOnlyCustomSize) && (
                  <div className="space-y-1.5">
                    {showOnlyCustomSize && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-gray-700">Custom Dimensions</span>
                      </div>
                    )}
                    <div className="rounded-xl border border-gray-200 bg-white p-3 space-y-2.5 shadow-sm">
                      <div className="flex items-center gap-3">
                        {supportedUnits.length > 1 && (
                          <div className="flex flex-col items-center gap-1.5 pr-3 border-r border-gray-200">
                            {supportedUnits.map(unit => {
                              const displayUnit = unit.toLowerCase() === 'inch' ? 'IN' : unit.toLowerCase() === 'ft' ? 'FT' : unit.toUpperCase();
                              return (
                                <button
                                  key={unit}
                                  type="button"
                                  onClick={() => setCustomUnit(unit)}
                                  className={`rounded-md px-2.5 py-1 text-[10px] font-bold transition-colors ${customUnit === unit
                                    ? "bg-[#e53935] text-white shadow-sm"
                                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                    }`}
                                >
                                  {displayUnit}
                                </button>
                              );
                            })}
                          </div>
                        )}
                        <div className="flex flex-1 items-center gap-2">
                          <div className="flex-1">
                            <label className="mb-1 block text-xs font-medium text-gray-500">
                              Width ({customUnit.toLowerCase() === 'inch' ? 'IN' : customUnit.toLowerCase() === 'ft' ? 'FT' : customUnit.toUpperCase()})
                            </label>
                            <input
                              type="number"
                              step="any"
                              min={customSizeConfig?.min_width || 0.1}
                              max={customSizeConfig?.max_width || 9999}
                              value={customWidth}
                              onChange={(e) => setCustomWidth(e.target.value)}
                              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-900 outline-none transition focus:border-[#e53935] bg-white"
                              placeholder="Width"
                            />
                          </div>
                          <div className="self-end pb-2 font-bold text-gray-400">×</div>
                          <div className="flex-1">
                            <label className="mb-1 block text-xs font-medium text-gray-500">
                              Height ({customUnit.toLowerCase() === 'inch' ? 'IN' : customUnit.toLowerCase() === 'ft' ? 'FT' : customUnit.toUpperCase()})
                            </label>
                            <input
                              type="number"
                              step="any"
                              min={customSizeConfig?.min_height || 0.1}
                              max={customSizeConfig?.max_height || 9999}
                              value={customHeight}
                              onChange={(e) => setCustomHeight(e.target.value)}
                              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-900 outline-none transition focus:border-[#e53935] bg-white"
                              placeholder="Height"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Live Area & Cost Calculation Breakdown */}
                      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-[#faf8ed] border border-[#f5eedc] px-3 py-2 text-xs">
                        <div className="flex items-center gap-1.5">
                          <span className="text-gray-600 font-medium">Area:</span>
                          <span className="font-bold text-gray-900">
                            {customArea} {unitAreaLabel}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-gray-500">
                            {customUnitRate > 0 ? `(@ ₹${formatRupees(customUnitRate)}/${unitAreaLabel})` : ""}
                          </span>
                          <span className="font-bold text-[#d32f2f] text-sm">
                            = ₹{formatRupees(customSizePrice)}
                          </span>
                          <span className="text-[10px] text-gray-500 font-normal">/ piece</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <OptionRow label="Material" options={materialOptions} value={selectedMaterial} onChange={setSelectedMaterial} icon={FaLayerGroup} defaultUnit={defaultUnit} productData={data} />
            <OptionRow label="Printing Side" options={printingSideOptions} value={selectedPrintingSide} onChange={setSelectedPrintingSide} icon={FaTag} defaultUnit={defaultUnit} productData={data} />
            <OptionRow label="Colour" options={colourOptions} value={selectedColour} onChange={setSelectedColour} icon={FaPalette} defaultUnit={defaultUnit} productData={data} />
            <OptionRow label="Finish" options={filteredFinishingOptions} value={selectedFinishing} onChange={setSelectedFinishing} icon={FaTint} defaultUnit={defaultUnit} productData={data} />
          </div>
        </div>
      )}

      {/* Delivery Option Dropdown */}
      {!isSpecialRequirement && deliveryMode === "weekday" && weekdayOptions.length > 0 && (
        <DeliveryDropdown
          options={weekdayOptions}
          value={selectedWeekdayOptionIndex}
          onChange={setSelectedWeekdayOptionIndex}
          priceModifiers={isCustomSizeActive ? 0 : priceModifiers}
          label="Bulk Order Option"
          isBulk
          defaultUnit={defaultUnit}
          isCustomSize={isCustomSizeActive}
          customUnitRate={customUnitRate}
          unitAreaLabel={unitAreaLabel}
        />
      )}

      {!isSpecialRequirement && deliveryMode === "express" && expressOptions.length > 0 && (
        <DeliveryDropdown
          options={expressOptions}
          value={selectedExpressOptionIndex}
          onChange={setSelectedExpressOptionIndex}
          priceModifiers={isCustomSizeActive ? 0 : priceModifiers}
          label="Regular Order Option"
          defaultUnit={defaultUnit}
          isCustomSize={isCustomSizeActive}
          customUnitRate={customUnitRate}
          unitAreaLabel={unitAreaLabel}
        />
      )}

      {/* Quantity Control — shown for all order modes except Special Requirement */}
      {!isSpecialRequirement && (
        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-bold text-gray-900 whitespace-nowrap">{data?.quantity_label?.trim() || "Qty"}</span>
            <div className="flex items-center gap-3">
              <QuantityStepper
                value={quantity}
                min={qtyMin}
                max={qtyMax}
                step={qtyStep}
                onChange={setQuantity}
              />
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-bold text-[#d32f2f]">
                  ₹{formatRupees(unitPrice)}
                </span>
                <span className="text-sm font-medium text-[#d32f2f] capitalize">
                  /{isCustomSizeActive ? "piece" : (activeDeliveryOption?.unit || selectedExpressOption?.unit || defaultUnit)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* File Upload Option */}
      {!isSpecialRequirement && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-gray-900">डिज़ाइन फाइल भेजें</h3>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setFileOption("whatsapp")}
                className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${fileOption === "whatsapp"
                  ? "border-green-500 bg-green-500 text-white"
                  : "border-gray-200 bg-white text-gray-600 hover:bg-green-50 hover:text-green-600"
                  }`}
              >
                <FaWhatsapp className="text-lg" />
                <span>WhatsApp</span>
              </button>
              <button
                type="button"
                onClick={() => setFileOption("email")}
                className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${fileOption === "email"
                  ? "border-[#e53935] bg-[#e53935] text-white"
                  : "border-gray-200 bg-white text-gray-600 hover:bg-red-50 hover:text-[#e53935]"
                  }`}
              >
                <FaEnvelope className="text-lg" />
                <span>Email</span>
              </button>
            </div>
          </div>
          <p className="text-xs leading-relaxed text-gray-500">
            फाइल भेजें :{" "}
            <a href={fileOption === "whatsapp" ? "https://wa.me/918989801088" : "mailto:print@indorinama.in"} className="font-semibold text-[#e53935] hover:underline">
              {fileOption === "whatsapp" ? "+91 8989801088" : "print@indorinama.in"}
            </a>{" "}
            | ऑर्डर के बाद इनवॉइस भेजें।
          </p>
        </div>
      )}

      {/* Coupon Input */}
      {!isSpecialRequirement && (
        <div className="space-y-2">
          <h3 className="text-sm font-bold text-gray-900">Promo Code (कूपन कोड)</h3>
          {!couponApplied ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
                placeholder="कूपन कोड डालें"
                className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-[#e53935] focus:ring-1 focus:ring-[#e53935]"
                onKeyDown={(e) => e.key === "Enter" && handleApplyCoupon()}
              />
              <button
                type="button"
                onClick={handleApplyCoupon}
                className="rounded-lg bg-[#e53935] px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-[#d32f2f]"
              >
                Apply
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between rounded-lg border border-dashed border-green-300 bg-green-50 px-3 py-2">
              <div>
                <span className="text-xs font-semibold text-green-700">{couponCode.toUpperCase()}</span>
                <p className="text-[10px] text-green-600">{appliedDiscountPercentage}% off applied</p>
              </div>
              <button
                type="button"
                onClick={handleRemoveCoupon}
                className="rounded-md bg-green-100 px-2.5 py-1 text-[10px] font-semibold text-green-700 transition hover:bg-green-200"
              >
                Remove
              </button>
            </div>
          )}
          {couponError && (
            <p className="text-xs text-red-500">{couponError}</p>
          )}
        </div>
      )}

      {/* Price Summary */}
      {!isSpecialRequirement && (
        <div className="rounded-xl border border-[#f5eedc] bg-[#fce8e6] p-4">
          <div className="flex items-start gap-4">
            <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#fdeecf] text-[#e69b00]">
              <FaFileAlt className="text-lg" />
            </div>
            <div className="flex-1 space-y-1.5 text-sm">
              {/* Custom Size Breakdown */}
              {isCustomSizeActive && (
                <div className="flex items-center justify-between text-xs text-gray-700 bg-[#f4ebd0]/50 -mx-2 px-2.5 py-1.5 rounded-lg border border-[#ede3c5]">
                  <span>
                    Custom Size ({numWidth} × {numHeight} {customUnit} = {customArea} {unitAreaLabel})
                  </span>
                  <span className="font-semibold text-gray-900">
                    ₹{formatRupees(customUnitRate)}/{unitAreaLabel} → ₹{formatRupees(customSizePrice)}
                  </span>
                </div>
              )}

              {/* Unit Price */}
              <div className="flex items-center justify-between text-gray-800">
                <span>{deliveryMode === "weekday" ? "Bulk Package Price" : "Unit Price"}</span>
                <PriceWithUnit
                  price={unitPrice}
                  unit={isCustomSizeActive ? "piece" : currentUnit}
                  className="font-medium"
                />
              </div>

              {/* Bulk Savings */}
              {deliveryMode === "weekday" && Number(selectedWeekdayOption?.original_price) > Number(selectedWeekdayOption?.price) && (
                <div className="flex items-center justify-between text-green-600 text-xs font-semibold">
                  <span>Bulk Discount Savings</span>
                  <span>-₹{formatRupees(Number(selectedWeekdayOption.original_price) - Number(selectedWeekdayOption.price))}</span>
                </div>
              )}

              {/* Quantity breakdown */}
              {qty > 1 && (
                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span>₹{formatRupees(unitPrice)} × {qty}</span>
                  <span>₹{formatRupees(subtotal)}</span>
                </div>
              )}

              {/* Subtotal */}
              <div className="flex items-center justify-between text-gray-800">
                <span>Subtotal</span>
                <span className="font-medium">₹{formatRupees(subtotal)}</span>
              </div>

              {/* Discount */}
              {couponApplied && discountAmount > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-green-600">Discount ({appliedDiscountPercentage}%)</span>
                  <span className="font-medium text-green-600">
                    -₹{formatRupees(discountAmount)}
                  </span>
                </div>
              )}

              {/* GST */}
              <div className="flex items-center justify-between text-gray-800">
                <span>GST (18%)</span>
                <span className="font-medium">₹{formatRupees(gst)}</span>
              </div>

              {/* Delivery */}
              <div className="flex items-center justify-between text-gray-800">
                <span>
                  Delivery
                  {activeDeliveryTier && totalAreaSqFt != null && (
                    <span className="ml-1.5 text-[11px] font-normal text-gray-400">
                      ({totalAreaSqFt} sq ft · up to {formatRupees(activeDeliveryTier.limit)} sq ft)
                    </span>
                  )}
                </span>
                <span className="font-medium">₹{formatRupees(resolvedDeliveryCharge)}</span>
              </div>

              <div className="border-t border-[#f0e6d2] my-2 pt-2" />

              {/* Total */}
              <div className="flex items-center justify-between">
                <span className="text-base font-bold text-gray-900">कुल राशि (Total)</span>
                <span className="text-xl font-bold text-[#d32f2f]">₹{formatRupees(total)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrintingCart;
